import React, {useContext} from 'react';
import {useAppStore} from "../../../../GlobalStore/GlobalStore.ts";
import {GlobalContext} from "../../../../App.tsx";
import { FilePathDisplay } from '../Content/TextComponents/shared/FilePathDisplay';
import save from "../../../../assets/save.svg";
import cancel from "../../../../assets/cancel.svg";

interface FileChangeButtonsProps {}

interface FileTrackerData {
    operation: 'create' | 'edit' | 'move' | 'delete';
    originalFilePath?: string;
    originalContent?: string;
}

interface UnreviewedFileTracker {
    [filePath: string]: FileTrackerData;
}

export const handleAcceptFile = (filePath: string, environment: string, vscode: any, updateUnreviewedFileTracker: any) => {
    const message = {
        functionName: 'acceptFile',
        data: [{filePath: filePath}]
    };

    if (environment.includes("jetbrains")) {
        window.jetbrainsPostMessage(JSON.stringify(message));
    } else if (environment === "vscode") {
        vscode.postMessage(message);
    }

    updateUnreviewedFileTracker(filePath, null, "delete");
}

const UnreviewedFiles: React.FC<FileChangeButtonsProps> = ({ }) => {
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const {environment, vscode} = environmentObj;
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const {unreviewedFileTracker, canSubmit} = dynamicStateObj;
    const updateUnreviewedFileTracker = useAppStore((state: any) => state.updateUnreviewedFileTracker);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const {dynamicStateObjRef} = useContext(GlobalContext);

    if (!canSubmit || Object.keys(unreviewedFileTracker).length === 0) return null;

    const handleRejectFile = (filePath: string) => {
        const message = {
            functionName: 'rejectFile',
            data: [{filePath: filePath}]
        };

        if (environment.includes("jetbrains")) {
            window.jetbrainsPostMessage(JSON.stringify(message));
        } else if (environment === "vscode") {
            vscode.postMessage(message);
        }

        updateUnreviewedFileTracker(filePath, null, "delete");
    }

    const handleAcceptAll = () => {
        Object.entries(dynamicStateObjRef.current.unreviewedFileTracker).forEach(([filePath]) => {
            handleAcceptFile(filePath, environment, vscode, updateUnreviewedFileTracker);
        });

        setDynamicStateObj({unreviewedFileTracker: {}});
    }

    const handleRejectAll = () => {
        Object.entries(dynamicStateObjRef.current.unreviewedFileTracker).forEach(([filePath]) => {
            handleRejectFile(filePath);
        });

        setDynamicStateObj({unreviewedFileTracker: {}});
    }

    const operationOrder = {
        'create': 1,
        'edit': 2,
        'move': 3,
        'delete': 4
    } as const;

    const sortedFiles = Object.entries(unreviewedFileTracker as UnreviewedFileTracker)
        .sort(([filePathA, dataA], [filePathB, dataB]) => {
            const orderA = operationOrder[dataA.operation];
            const orderB = operationOrder[dataB.operation];
            if (orderA !== orderB) return orderA - orderB;
            return filePathA.localeCompare(filePathB);
        });



    return (
        <div className="flex flex-col gap-2.5 p-2 mb-2">
            <div className="px-3.5 py-2.5 border rounded-xl border-neutral-500 dark:border-neutral-700 border-opacity-35 dark:border-opacity-25">
                <div className="ml-1 text-base text-neutral-600 dark:text-neutral-400 mb-2">Unreviewed Changes</div>
                <div className="space-y-3">
                    {(['create', 'edit', 'move', 'delete'] as const).map(operation => {
                        const filesForOperation = sortedFiles.filter(([_, data]) => data.operation === operation);
                        if (filesForOperation.length === 0) return null;

                        return (
                            <div key={operation} className="space-y-2">
                                <div className="text-sm font-medium ml-1 text-neutral-600 dark:text-neutral-400">
                                    {operation === 'create' ? 'Created' :
                                     operation === 'edit' ? 'Edited' :
                                     operation === 'move' ? 'Moved' : 'Deleted'}
                                </div>
                                {filesForOperation.map(([filePath, data]) => (
                                    <div key={filePath} className="flex items-center justify-between py-1">
                                        <FilePathDisplay
                                            filePath={filePath}
                                            operation={data.operation}
                                            originalFilePath={data.originalFilePath}
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleRejectFile(filePath)}
                                                className="h-4 w-4 cursor-pointer"
                                            >
                                                <img src={cancel} alt="Reject" />
                                            </button>
                                            <button
                                                onClick={() => handleAcceptFile(filePath, environment, vscode, updateUnreviewedFileTracker)}
                                                className="h-4 w-4 cursor-pointer"
                                            >
                                                <img src={save} alt="Accept" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="flex gap-1.5 ml-1.5">
                <button 
                    onClick={handleAcceptAll}
                    className="px-2 py-0.5 text-xs rounded bg-green-100 hover:bg-green-200 text-green-700 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-300"
                >
                    Accept All
                </button>
                <button 
                    onClick={handleRejectAll}
                    className="px-2 py-0.5 text-xs rounded bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-300"
                >
                    Reject All
                </button>
            </div>
        </div>
    );
};

export default UnreviewedFiles;